import {Component, effect, inject, signal} from '@angular/core';
import {PageComponent} from '../../shared/page/page.component';
import {EntityListComponent} from '../../shared/entity-list/entity-list.component';
import {SearchService, SearchTypeFilter} from '../../../services/search.service';
import {debounceTime, filter, map, Observable, switchMap, tap} from 'rxjs';
import {PagedList} from '../../../models/entity.model';
import {PackItemModel} from '../../../models/pack-item.model';
import {FormsModule} from '@angular/forms';
import {DropDownButtonComponent} from '../../shared/drop-down-button/drop-down-button.component';
import {IconComponent} from '../../shared/icon/icon.component';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';

interface SearchQuery {
  query: string
  typeFilter: SearchTypeFilter,
  source: 'user' | 'params'
}

const MARKETPLACE_NAME = 'Marketplace'

@Component({
  selector: 'app-marketplace-page',
  imports: [
    PageComponent,
    EntityListComponent,
    FormsModule,
    DropDownButtonComponent,
    IconComponent
  ],
  templateUrl: './marketplace-page.component.html',
  host: {
    class: 'app-container'
  }
})
export class MarketplacePageComponent {
  protected readonly searchQuery = signal<SearchQuery>({ query: '', typeFilter: null, source: 'params' })
  protected pageTitle = signal<string>(MARKETPLACE_NAME)
  protected readonly results: Observable<PagedList<PackItemModel>>

  private readonly searchService = inject(SearchService)
  private readonly activeRoute = inject(ActivatedRoute)
  private readonly router = inject(Router)

  constructor() {
    effect(() => {
      const sq = this.searchQuery()

      if (sq.source === 'params') return
      this.router.navigate(
        [],
        {
          queryParams: { q: sq.query.length > 0 ? sq.query : null, type: sq.typeFilter },
          queryParamsHandling: 'replace',
          replaceUrl: true
        }
      )
    })

    this.results = this.activeRoute.queryParamMap.pipe(
      map<ParamMap, SearchQuery>(params => {
        return {
          query: params.get('q') || '',
          typeFilter: params.get('type') as SearchTypeFilter || null,
          source: 'params'
        }
      }),
      filter(sq => sq.query.length === 0 || sq.query.length >= 3),
      debounceTime(300),
      tap(sq => {
        this.searchQuery.set(sq)
        this.updateTitle()
      }),
      switchMap(query => this.searchService.search(query.query, query.typeFilter).pipe())
    )
  }

  protected onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement
    this.searchQuery.update(val => {
      return { ...val, query: input.value, source: 'user' }
    })
  }

  protected setTypeFilter(typeFilter: SearchTypeFilter) {
    this.searchQuery.update(val => {
      return { ...val, typeFilter, source: 'user' }
    })
  }

  private updateTitle() {
    const sq = this.searchQuery()
    const titleParts: string[] = []

    if (sq.query.length > 0) {
      titleParts.push(`"${sq.query}"`)
    }
    if (sq.typeFilter) {
      titleParts.push(sq.typeFilter === 'pack' ? 'Packs' : 'Prompts')
    }
    if (titleParts.length > 0) {
      titleParts.push('-')
    }
    titleParts.push(MARKETPLACE_NAME)

    this.pageTitle.set(titleParts.join(' '))
  }
}
